import React from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {IconTooltip, tokensForThemeProvider} from '@forecast-it/design-system';
import {isRateRoundedForDisplay} from '../../../forecast-app/shared/util/RoundingUtil';

/** Matches the design-system input heights, so the icon centres on the input rather than on its label. */
const INPUT_HEIGHT = {small: '32px', large: '44px'};

const Row = styled.div`
	display: flex;
	align-items: flex-end;
	gap: ${tokensForThemeProvider.sky.sys.spacing['100']};
	max-width: 100%;
`;

const Field = styled.div`
	flex: 1;
	min-width: 0;
`;

const IconSlot = styled.div<{$height: string}>`
	flex: 0 0 16px;
	height: ${props => props.$height};
	display: flex;
	align-items: center;
`;

interface RateFullPrecisionFieldProps {
	rate: number | null | undefined;
	currencyCode: string;
	size: 'small' | 'large';
	children: React.ReactNode;
}

/**
 * Wraps a rate input that displays rates rounded to 4 decimal places. When the rate it shows has more
 * precision than that, an info icon beside it reveals the full stored rate on hover, labelled as the exact rate.
 *
 * The icon sits outside the input because a disabled input does not receive hover. The slot is always
 * rendered so that fields in a column keep the same width whether or not they show the icon.
 */
const RateFullPrecisionField = ({rate, currencyCode, size, children}: RateFullPrecisionFieldProps): JSX.Element => {
	const intl = useIntl();
	const showFullRate = rate != null && isRateRoundedForDisplay(rate);

	return (
		<Row>
			<Field>{children}</Field>
			<IconSlot $height={INPUT_HEIGHT[size]} data-testid="rate-full-precision-slot">
				{showFullRate && (
					<span data-cy="rate-full-precision-icon">
						<IconTooltip icon="info" iconSize="s" width={IconTooltip.WIDTH.s}>
							<IconTooltip.Item
								title={intl.formatMessage({id: 'rate_card_modal.exact_rate'})}
								details={intl.formatNumber(rate, {
									style: 'currency',
									currency: currencyCode,
									minimumFractionDigits: 2,
									maximumFractionDigits: 9,
								})}
							/>
						</IconTooltip>
					</span>
				)}
			</IconSlot>
		</Row>
	);
};

export default RateFullPrecisionField;
